import { el, mount, unmount, setStyle, setAttr } from "redom"
import { fetchPostJsonIfValid, handleErrorWithAlert } from "../../utils/fetch-json.js"
import { createFormData } from "../../utils/formdata.js"
import { isBlocked, updateBlockList } from "../helpers/report.js"
import {Modal} from 'bootstrap'
import type { FormMap, ModalButton, UserDetails } from "#/types"

// Note: This is very different from the createRecordDeletionModal in recordPost.js
// This uses event target's record-title to handle different records at one view.
export const createRecordDeletionModalElement = () => { // returns HTMLElement
    const deleteTitleBox = el("p#delete-record-title", { style: "text-align: center; text-overflow: ellipsis; overflow: hidden; font-size: 25; margin: 0px;" })
    const modal = new SubmitModal("deleteConfirm", "DELETE RECORD", deleteTitleBox, "DELETE", () => {})
    const modalEl = modal.el;
    modalEl.addEventListener("show.bs.modal", (event) => {
        const modalButton = (event as any).relatedTarget; // Button that triggered the modal
        if (!modalButton) return;
        const recordTitle = modalButton.getAttribute("data-record-title");
        if (recordTitle && deleteTitleBox) { // Note: Not to call RecordDeletionModal from recordPost.js
            deleteTitleBox.textContent = recordTitle
            const confirmButton = modal.submitButton
            const fileId = modalButton.getAttribute("data-file-id");
            if (fileId && confirmButton) {
                confirmButton.onclick = () => {
                    modal.disableSubmitButton()
                    fetchPostJsonIfValid("/v1/r/" + fileId + "/delete").then((r: FormMap ) => {
                        if (r?.['success']) {
                            const recordEl = document.getElementById(fileId);
                            if (recordEl) recordEl.style.display = "none";
                        }
                    }).catch(handleErrorWithAlert).finally(() => modal.enableSubmitButton())
                };
            }
    
        }
    })
    return modalEl
}

class DefaultModal {
    el: HTMLElement;
    header: HTMLDivElement;
    content: HTMLDivElement;
    dialog: HTMLDivElement;
    body: HTMLDivElement;
    title!: HTMLHeadingElement;
    constructor(modalId: string, bodyHtml: HTMLElement) {
        this.el = el(".modal fade#" + modalId, { tabindex: "-1", role: "dialog", "aria-labelledby" : modalId, "area-hidden": true },
            this.dialog = el(".modal-dialog modal-dialog-centered display-in-safearea",
                this.content = el(".modal-content",
                    this.header = el(".modal-header"),
                    this.body = el(".modal-body", bodyHtml)
                )
            )
        )
    }
    removeHeaderBorderBottom() {
        this.header.style.borderBottom = "none"
    }
    showHeaderBorderBottom() {
        this.header.style.borderBottom = "1px solid #dee2e6"
    }
    setTitle(title: string) {
        mount(this.header, this.title = el("h6.subHead", title))
    }
    removeTitle() {
        unmount(this.header, this.title)
    }
    displayCloseButton() {
        mount(this.header, el("button.close", { type: "button", "data-bs-dismiss": "modal", "aria-label": "Close"}, el("span", { "aria-hidden": "true", textContent: "x" })))
    }
    setLongModal() {
        this.dialog.classList.add("modal-dialog-scrollable")
    }
    noPaddingInBody() {
        setStyle(this.body, { padding: 0 })
    }
    setDefaultPaddingInBody() {
        this.body.style.removeProperty("padding");
    }
}


class MenuModal extends DefaultModal {
   modals: DefaultModal[];
    constructor(modalId: string, contents: ModalButton[]) {
        const bodyHtml = el("div")
        contents.forEach( content => {
            const elem = el("div.modalBtn clickable.menu", { style: "display: block; padding: 0 1rem;" },
                el("span.clickable content", { style: "margin: 5px 0px;"}, content.icon),
                el("span.clickable content", { style: "margin: 5px 10px; " }, content.text))
            if (content.setToolTip == undefined) setAttr(elem, { "data-bs-dismiss": "modal" })

            if (content.action != undefined) elem.onclick = content.action
            else if (content.modal != undefined) elem.onclick = () => {
                Modal.getOrCreateInstance(content.modal.el).show()
            }
            else if (content.setToolTip != undefined) content.setToolTip(elem)
            mount(bodyHtml, elem)
        })

        super(modalId, bodyHtml)
        this.displayCloseButton()
        this.modals = contents.map( s => s.modal ).filter(s => s != undefined)
        this.noPaddingInBody()
    }
}

export const createMenuButton = (id: string, buttons: ModalButton[], container: HTMLElement| DocumentFragment) => {
    if (buttons.length == 0) return el("")
    const modal = new MenuModal(id, buttons)
    mount(container, modal)
    modal.modals.forEach(m => mount(container, m.el))
    return el("button.modalBtn clickable", { type: "button", 'data-bs-toggle': "modal", 'data-bs-target': `#${id}`, onclick: () => { 
            Modal.getOrCreateInstance(modal.el).show()
        }}, el("i.fa fa-ellipsis-h"))
}

export class ShareModal extends DefaultModal {
    constructor(bodyHtml: HTMLElement) {
        super("shareModal", bodyHtml)
        this.setTitle("SHARE")
    }
}

export class SubmitModal extends DefaultModal {
   submitButton: HTMLButtonElement;
    constructor(modalId: string, title: string, bodyHtml: HTMLElement, submitTitle: string, onclickAction: () => void = () => {}) {
        super(modalId, bodyHtml)
        this.setTitle(title)
        mount(this.content, el(".modal-footer",
            el("button.btn btn-sm btn-default cancel-button", { type: "button", "data-bs-dismiss": "modal" },  "CANCEL" ),
            this.submitButton = el("button.btn btn-sm btn-danger#" + modalId + submitTitle, { type: "button", onclick: onclickAction, "data-bs-dismiss": "modal" }, submitTitle ) as HTMLButtonElement)
        )
        this.displayCloseButton()
        this.noPaddingInBody()
    }
    disableSubmitButton() {
        this.submitButton.disabled = true
    }
    enableSubmitButton() {
        this.submitButton.disabled = false
    }
    open(onclickAction: () => void) {
        this.submitButton.onclick = onclickAction
        Modal.getOrCreateInstance(this.el).show()
    }
}

export class EditModal extends SubmitModal {
    constructor(modalId: string, title: string, bodyHtml: HTMLElement, submitTitle: string, onclickAction: () => void) {
        super(modalId, title, bodyHtml, submitTitle, onclickAction)
        this.setDefaultPaddingInBody()
    }
}

export const createReportUserButton = (uploader: UserDetails, userId?: string, comment?: string, commentCreatedAt?: number, urlToOpenAfterReport?: string) => {
    const reportButton: ModalButton = { icon: el("i.fas fa-exclamation-triangle fa-fw"), text: "Report" }
    if (isBlocked(uploader.id)) {
        reportButton.action = () => alert(`You have already blocked and reported ${uploader.name}!`)
        return reportButton
    }
    if (userId == undefined) {
        reportButton.setToolTip = (elem: HTMLElement) => setSignInModal(elem, "Report this user?", "to report!")
        return reportButton
    }

    const content = el("div", { style: "text-align: left; margin: 1rem; "},
        el("p.report-modal-top-text", { style: "font-size: 0.9rem;" }, ` You will no longer be able to see ${uploader.name}'s records, comments, and likes. If you have followed ${uploader.name}, reporting will cancel it.`),
        el("span.report-modal-question", { style: "font-size: 1.0rem; font-weight: 500" }, " What made you report?")
    )
    const report = () => {
        const checkedReason = reasons.find(r => r.checked)
        const reason = checkedReason != undefined ? checkedReason.value : otherOpinion.value // can't use optional chaining in iOS 12.5.5
        fetchPostJsonIfValid("/v1/u/" + uploader.id + "/report", createFormData({ url: window.location.href, reason, comment, commentCreatedAt })).then((r: FormMap) => {
            if (r?.['success']) {
                updateBlockList(uploader.id)
                fetchPostJsonIfValid("/v1/u/" + uploader.id + "/unfollow")
                window.location.href = urlToOpenAfterReport != undefined ? urlToOpenAfterReport : "/records" // can't use optional chaining in iOS 12.5.5
                alert(`You've successfully blocked and reported ${uploader.name}!`)
            }
        }).catch(handleErrorWithAlert)
    }
    const reportModal = new SubmitModal(`report_user_modal-${uploader.id}-${userId}-${commentCreatedAt ?? ""}`, `Report ${uploader.name}?`, content, "REPORT", report)

    const reasonOptions = [ "Spam/Unauthorized sales", "Violence/Harassment", "Nudity", "False information" ]
    const reasons = reasonOptions.map( (reason) => {
        return el("input", { type: "radio", name: "report_reason", value: reason, style: "cursor: pointer; margin: 5px 0;" })
    })
    const radioButtonRowStyle = "margin: 5px 0;"
    const radioTextStyle = "margin: 5px 5px 5px 10px; cursor: pointer; font-size: 0.85rem;"
    reasons.forEach(r => {
        r.onchange = () => reportModal.enableSubmitButton()
        mount(content, el("div", { style: radioButtonRowStyle }, r, el("text.report-modal-option", { style: radioTextStyle, onclick: () => r.click()}, r.value, el("br"))))
    })

    const otherButton = el("input", { type: "radio", name: "report_reason", style: "cursor: pointer; color: #555;", onclick: () => setSubmitButton() })
    const otherOpinion = el("input.other-reason", { type: "text", placeholder: "Write down..", oninput: () => setSubmitButton(), onclick: () => {
        otherButton.click()
        setSubmitButton()
    }})
    const setSubmitButton = () => {
        if (otherOpinion.value == "") reportModal.disableSubmitButton()
        else reportModal.enableSubmitButton()
    }
    mount(content, el("div", { style: radioButtonRowStyle },otherButton, el("text.report-modal-option", { style: radioTextStyle, onclick: () => otherButton.click() }, "Other", el("br"))))
    mount(content, otherOpinion)

    reportModal.showHeaderBorderBottom()
    reportModal.disableSubmitButton()
    reportModal.setLongModal()

    reportButton.modal = reportModal
    return reportButton
}

export class LongModal extends DefaultModal {
    constructor(modalId: string, title: string, bodyHtml: HTMLElement) {
        super(modalId, bodyHtml)
        this.setTitle(title)
        this.displayCloseButton()
        this.setLongModal()
    }
}

export const setSignInModal = (element: HTMLElement, title: string, textToDo: string) => {
    element.classList.add("signInModal")
    setAttr(element, {
        "data-bs-toggle": "popover",
        "data-bs-original-title": title,
        "data-bs-content": `<a href="#" class="signInLink"><u><b>Sign in</b></u></a> ${textToDo}`,
        "data-bs-placement": "right"
    })
}
